// Baylor University: public college page (#2746), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Baylor's Common Data Set 2025-2026 (Institutional Research,
//    cds-pdf-2025-2026-final.pdf): C1 applicants, admits and enrollees for
//    Fall 2025 with the residency table, C8 test policy for Fall 2027,
//    C21/C22 plans with the early decision counts, G1/G5 2026-2027 costs.
//  - Baylor Admissions, 2027-2028 Application Process (the plans for
//    students entering in the 2027-2028 year, fall 2027): deadlines,
//    decision and deposit dates, and the test-optional choice. Baylor's site
//    refused scripted reads on the day (a bot check), so the page was read
//    through a fetch tool; the CDS C21/C22 dates agree with it.
//  - College Scorecard, school id 223232 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Cost: CDS G1 tuition and on-campus housing and food, with the G5
// resident estimates for books, transportation and other expenses. G1
// lists no separate required fees.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Baylor Common Data Set 2025-2026',
        url: 'https://ir.web.baylor.edu/sites/g/files/ecbvkj1621/files/2026-04/cds-pdf-2025-2026-final.pdf',
        read: '2026-10-01',
    },
    process: {
        label: 'Baylor Admissions, 2027-2028 Application Process',
        url: 'https://admissions.web.baylor.edu/admissions/incoming-freshman/application-process',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?223232-Baylor-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'baylor-university',
    kbId: 'baylor_university',
    name: 'Baylor University',
    shortName: 'Baylor',
    location: 'Waco, TX',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Baylor brand colors); decoration only.
    branding: {
        primary: '#154734',
        secondary: '#FFB81C',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/baylor_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 44924,
        admitted: 23459,
        enrolled: 3552,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 28416, admitted: 14636 },
            { group: 'Out-of-state', applied: 12955, admitted: 7061 },
            { group: 'International', applied: 3553, admitted: 1762 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Baylor\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by school or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early decision',
        note: 'The Common Data Set gives early decision counts but none for early action, which is not binding and not restrictive.',
        year: 'Fall 2025',
        rows: [
            { plan: 'Early Decision', applied: 566, admitted: 453 },
        ],
        source: 'cds',
        where: 'sections C21 and C22',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'February 1' },
        rows: [
            { plan: 'Early Decision', deadline: 'November 1', decision: 'No later than December 15', binding: true },
            { plan: 'Early Action', deadline: 'November 1', decision: 'No later than February 1', binding: false },
            { plan: 'Regular Decision', deadline: 'February 1', decision: 'No later than April 10', binding: false },
        ],
        note: 'The application opens August 1. The deposit is due February 15 for Early Decision and May 1 for Early Action and Regular Decision. The priority deadline for the FAFSA is February 1.',
        source: 'process',
        binding: 'Early Decision is binding: it is for students with Baylor as their first choice, and the student, a parent and a high school counselor sign a contract that the student will deposit and enroll if admitted. You may apply to other schools under non-binding plans, but if Baylor admits you early, you withdraw those applications.',
        bindingSource: 'cds',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Baylor does not require SAT or ACT scores. On the application you choose whether you want your scores reviewed, and Baylor treats that choice as final.',
        source: 'process',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as considered if submitted, not required, for fall 2027 applicants.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'process',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 67756 },
            { item: 'Housing (on campus)', amount: 9976 },
            { item: 'Food (on-campus meal plan)', amount: 7246 },
            { item: 'Books and supplies', amount: 1572 },
            { item: 'Transportation', amount: 1548 },
            { item: 'Other expenses', amount: 2466 },
        ],
        total: 90564,
        note: 'For a full-time undergraduate living on campus.',
        source: 'cds',
        where: 'sections G1 and G5',
        netPrice: {
            average: 44441,
            byIncome: [
                { income: '$0 to $30,000', amount: 45498 },
                { income: '$30,001 to $48,000', amount: 24146 },
                { income: '$48,001 to $75,000', amount: 33908 },
                { income: '$75,001 to $110,000', amount: 38607 },
                { income: 'Over $110,000', amount: 50541 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Baylor\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
